import { useState } from 'react';
import { ProspectCard } from './ProspectCard';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import { Prospect, SearchCriteria } from '@/pages/People';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface ProspectResultsProps {
  prospects: Prospect[];
  totalResults: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  searchCriteria: SearchCriteria | null;
}

export const ProspectResults = ({ 
  prospects, 
  totalResults, 
  currentPage, 
  onPageChange,
  searchCriteria 
}: ProspectResultsProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();
  const resultsPerPage = 25;
  const totalPages = Math.ceil(totalResults / resultsPerPage);

  const toggleSelectAll = () => {
    if (selectedIds.size === prospects.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(prospects.map(p => p.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const exportToCSV = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one prospect to export');
      return;
    }

    setIsExporting(true);
    
    try {
      const selectedProspects = prospects.filter(p => selectedIds.has(p.id));
      
      // Create CSV content
      const headers = ['Name', 'Email', 'Phone', 'Company', 'Title', 'LinkedIn URL', 'Industry'];
      const rows = selectedProspects.map(p => [
        p.name,
        p.email,
        p.phone,
        p.company,
        p.title,
        p.linkedinUrl,
        p.industry
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      const locationSlug = searchCriteria?.location.toLowerCase().replace(/[^a-z0-9]/g, '_');
      const titleSlug = searchCriteria?.jobTitle.toLowerCase().replace(/[^a-z0-9]/g, '_');
      a.href = url;
      a.download = `prospects_${titleSlug}_${locationSlug}_${timestamp}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${selectedIds.size} prospects successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export prospects');
    } finally {
      setIsExporting(false);
    }
  };

  const sendToPersonalizeOutreach = () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one prospect');
      return;
    }

    const selectedProspects = prospects.filter(p => selectedIds.has(p.id));
    // Store in localStorage temporarily for the other page to pick up
    localStorage.setItem('selectedProspects', JSON.stringify(selectedProspects));
    navigate('/personalize-outreach');
    toast.success(`Sending ${selectedIds.size} prospects to Personalize Outreach`);
  };

  return (
    <div className="space-y-6">
      <Card className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Checkbox
              checked={selectedIds.size === prospects.length}
              onCheckedChange={toggleSelectAll}
            />
            <span className="text-gray-900">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportToCSV}
              disabled={selectedIds.size === 0 || isExporting}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={sendToPersonalizeOutreach}
              disabled={selectedIds.size === 0}
            >
              <Send className="h-4 w-4 mr-2" />
              Send to Personalize Outreach
            </Button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            Showing {(((currentPage - 1) * resultsPerPage) + 1).toLocaleString()} - {Math.min(currentPage * resultsPerPage, totalResults).toLocaleString()} of {totalResults.toLocaleString()} results
          </p>
        </div>
      </Card>

      <div className="grid gap-4">
        {prospects.map((prospect) => (
          <ProspectCard
            key={prospect.id}
            prospect={prospect}
            isSelected={selectedIds.has(prospect.id)}
            onToggleSelect={toggleSelect}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>

            <span className="text-gray-900">
              Page {currentPage} of {totalPages}
            </span>

            <Button
              variant="outline"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};
